import React, { useEffect, useState } from 'react';
import { Card, Typography, Table, Tag, Space, Skeleton, Button, Tooltip, Empty } from 'antd';
import { 
  WarningOutlined, 
  CalendarOutlined,
  PhoneOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { dashboardService, UnpaidDiagnostic } from '../../services/dashboardService';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatCurrency = (amount: number | string): string => {
  const num = Number(amount) || 0;
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num) + ' FCFA';
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'pending': return 'orange';
    case 'assigned': return 'blue';
    case 'accepted': return 'cyan';
    case 'on_the_way': return 'geekblue';
    case 'arrived': return 'purple';
    case 'in_progress': return 'processing';
    case 'completed': return 'success';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'pending': return 'En attente';
    case 'assigned': return 'Assigné';
    case 'accepted': return 'Accepté';
    case 'on_the_way': return 'En route';
    case 'arrived': return 'Arrivé';
    case 'in_progress': return 'En cours';
    case 'completed': return 'Terminé';
    case 'cancelled': return 'Annulé';
    default: return status;
  }
};

const UnpaidDiagnosticsWidget: React.FC = () => {
  const [diagnostics, setDiagnostics] = useState<UnpaidDiagnostic[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getUnpaidDiagnostics(5);
        setDiagnostics(data);
      } catch (err) {
        console.error('Erreur chargement diagnostics non payés:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const columns: ColumnsType<UnpaidDiagnostic> = [
    {
      title: 'Client',
      key: 'customer',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.customer?.name || 'N/A'}</Text>
          {record.customer?.phone && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <PhoneOutlined /> {record.customer.phone}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Intervention',
      key: 'intervention',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text ellipsis style={{ maxWidth: 150 }}>{record.title}</Text>
          <Tag color={getStatusColor(record.status)} style={{ fontSize: '10px' }}>
            {getStatusLabel(record.status)}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      render: (date: string) => (
        <Space>
          <CalendarOutlined style={{ color: '#1890ff' }} />
          <Text>{formatDate(date)}</Text>
        </Space>
      ),
    },
    {
      title: 'Montant',
      dataIndex: 'diagnostic_fee',
      key: 'diagnostic_fee',
      render: (fee: number) => (
        <Text strong style={{ color: '#fa8c16' }}>{formatCurrency(fee)}</Text>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Tooltip title="Voir dans la liste">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            onClick={() => navigate(`/interventions`)}
          />
        </Tooltip>
      ),
    },
  ];

  if (loading) {
    return (
      <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Skeleton active paragraph={{ rows: 5 }} />
      </Card>
    );
  }

  const totalUnpaid = diagnostics.reduce((sum, d) => sum + Number(d.diagnostic_fee || 0), 0);

  return (
    <Card
      title={
        <Space>
          <WarningOutlined style={{ color: '#fa8c16' }} />
          <span>Diagnostics Non Payés</span>
          {diagnostics.length > 0 && (
            <Tag color="orange">{diagnostics.length}</Tag>
          )}
        </Space>
      }
      extra={
        diagnostics.length > 0 && (
          <Text strong style={{ color: '#fa8c16' }}>
            Total: {formatCurrency(totalUnpaid)}
          </Text>
        )
      }
      style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
    >
      {diagnostics.length === 0 ? (
        <Empty 
          description="Aucun diagnostic en attente de paiement"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          <Table
            dataSource={diagnostics}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="small"
          />
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button type="link" onClick={() => navigate('/interventions?filter=unpaid_diagnostic')}>
              Voir tous les diagnostics non payés
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default UnpaidDiagnosticsWidget;
